import React, { useState, useEffect } from "react";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../../../db/firebase";
import { AnimatedModal, modalPanelClass } from "../../../components/AnimatedModal";
import { LoadingSpinner } from "../../../components/Loading";
import { useTranslation } from "react-i18next";
import { HiOutlineXMark, HiOutlineEnvelope } from "react-icons/hi2";

interface InviteToEventModalProps {
  eventId: string;
  organizationId: string;
  onClose: () => void;
  leaders: string[];
  members: string[];
}

const InviteToEventModal: React.FC<InviteToEventModalProps> = ({
  eventId,
  onClose,
  leaders,
  members,
}) => {
  const { t } = useTranslation();
  const [users, setUsers] = useState<any[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [selectAll, setSelectAll] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [eventDetails, setEventDetails] = useState<any | null>(null);

  useEffect(() => {
    const fetchUsersAndEvent = async () => {
      setLoading(true);
      try {
        const userDocs = await Promise.all(
          [...leaders, ...members].map((userId) =>
            getDoc(doc(db, "users", userId))
          )
        );
        const userData = userDocs
          .map((d) => (d.exists() ? { id: d.id, ...d.data() } : null))
          .filter((u) => u !== null);
        setUsers(userData);

        const eventDoc = await getDoc(doc(db, "events", eventId));
        if (eventDoc.exists()) {
          setEventDetails({ id: eventId, ...eventDoc.data() });
        } else {
          setError("Event not found.");
        }
      } catch (err) {
        setError("Failed to fetch users or event details.");
      } finally {
        setLoading(false);
      }
    };
    fetchUsersAndEvent();
  }, [leaders, members, eventId]);

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting || selectedUsers.length === 0 || !eventDetails) {
      if (selectedUsers.length === 0) setError("Please select at least one user.");
      return;
    }
    setIsSubmitting(true);
    setError("");
    try {
      const response = await fetch("/.netlify/functions/sendEventNotification", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          emails: selectedUsers.map(
            (userId) => users.find((u) => u.id === userId)?.email || ""
          ),
          eventDetails,
        }),
      });
      if (response.ok) {
        setSuccess("Invitations sent successfully!");
        setSelectedUsers([]);
      } else {
        const data = await response.json();
        setError(data.error || "Failed to send invitations");
      }
    } catch (err) {
      setError("Failed to send invitations. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCheckboxChange = (userId: string) => {
    setSelectedUsers((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    );
  };

  const handleSelectAll = () => {
    if (selectAll) setSelectedUsers([]);
    else setSelectedUsers(users.map((u) => u.id));
    setSelectAll(!selectAll);
  };

  useEffect(() => {
    setSelectAll(selectedUsers.length === users.length && users.length > 0);
  }, [selectedUsers, users]);

  return (
    <AnimatedModal isOpen onClose={onClose} contentClassName="sm:max-w-lg md:max-w-2xl lg:max-w-3xl">
      <div className={modalPanelClass}>
        <div className="sm:hidden flex justify-center pt-2 pb-1 shrink-0">
          <div className="w-10 h-1 rounded-full bg-gray-300 dark:bg-gray-600" />
        </div>
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-700 bg-gradient-to-r from-gray-50 to-white dark:from-gray-800 dark:to-gray-800/80">
          <div className="flex items-center gap-2.5">
            <div className="flex items-center justify-center w-9 h-9 rounded-xl bg-[#5932EA]/10 dark:bg-[#5932EA]/20 text-[#5932EA]">
              <HiOutlineEnvelope className="w-5 h-5" strokeWidth={2} />
            </div>
            <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100">
              {t("inviteToEvent")}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-xl text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            aria-label={t("close")}
          >
            <HiOutlineXMark className="w-5 h-5" />
          </button>
        </div>
        <div className="p-5 max-h-[60vh] md:max-h-[75vh] overflow-y-auto">
          {loading ? (
            <div className="py-12">
              <LoadingSpinner size="lg" message={t("loading")} />
            </div>
          ) : (
            <form onSubmit={handleInvite} className="space-y-4">
              <div className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 dark:bg-gray-700/50">
                <input
                  type="checkbox"
                  id="select-all"
                  checked={selectAll}
                  onChange={handleSelectAll}
                  className="rounded border-gray-300 text-[#5932EA] focus:ring-[#5932EA]"
                />
                <label
                  htmlFor="select-all"
                  className="text-sm font-medium text-gray-700 dark:text-gray-300"
                >
                  Select All
                </label>
              </div>
              <ul className="space-y-2 max-h-48 overflow-y-auto">
                {users.map((user) => (
                  <li
                    key={user.id}
                    className="flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                  >
                    <input
                      type="checkbox"
                      id={`user-${user.id}`}
                      checked={selectedUsers.includes(user.id)}
                      onChange={() => handleCheckboxChange(user.id)}
                      className="rounded border-gray-300 text-[#5932EA] focus:ring-[#5932EA]"
                    />
                    <label
                      htmlFor={`user-${user.id}`}
                      className="text-gray-800 dark:text-gray-200 cursor-pointer flex-1"
                    >
                      {user.firstName} {user.lastName}
                    </label>
                  </li>
                ))}
              </ul>
              {error && (
                <p className="text-red-500 dark:text-red-400 text-sm">{error}</p>
              )}
              {success && (
                <p className="text-emerald-600 dark:text-emerald-400 text-sm">
                  {success}
                </p>
              )}
              <button
                type="submit"
                disabled={isSubmitting || selectedUsers.length === 0}
                className="w-full py-2.5 rounded-xl font-medium bg-[#5932EA] text-white hover:bg-[#4a28c7] disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <svg
                      className="animate-spin h-5 w-5"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    {t("sending") || "Sending..."}
                  </>
                ) : (
                  "Invite"
                )}
              </button>
            </form>
          )}
        </div>
      </div>
    </AnimatedModal>
  );
};

export default InviteToEventModal;
